/**
 * Pool hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { poolsApi, ApiError } from '../lib/api';
import { useAppStore } from '../stores/appStore';
import type { Pool, Ranking } from '../../shared/types';
import { normalizePoolDetailResponse, normalizePoolMutationResponse } from './pools-response-normalizer';

// Query keys
export const poolKeys = {
  all: ['pools'] as const,
  lists: () => [...poolKeys.all, 'list'] as const,
  list: (filters?: object) => [...poolKeys.lists(), filters] as const,
  details: () => [...poolKeys.all, 'detail'] as const,
  detail: (slug: string) => [...poolKeys.details(), slug] as const,
  rankings: () => [...poolKeys.all, 'ranking'] as const,
  ranking: (slug: string) => [...poolKeys.rankings(), slug] as const,
  members: (slug: string) => [...poolKeys.all, 'members', slug] as const,
};

/**
 * Hook to list all pools
 */
export function usePools() {
  return useQuery({
    queryKey: poolKeys.list(),
    queryFn: async () => {
      const response = await poolsApi.list();
      return response.data as Pool[];
    },
  });
}

/**
 * Hook to get single pool details
 */
export function usePool(slug: string) {
  return useQuery({
    queryKey: poolKeys.detail(slug),
    queryFn: async () => {
      const response = await poolsApi.get(slug);
      return normalizePoolDetailResponse(response.data);
    },
    enabled: !!slug,
  });
}

/**
 * Hook to get pool ranking
 */
export function usePoolRanking(slug: string) {
  return useQuery({
    queryKey: poolKeys.ranking(slug),
    queryFn: async () => {
      const response = await poolsApi.getRanking(slug);
      return response.data as Ranking[];
    },
    enabled: !!slug,
  });
}

/**
 * Hook to get pool members
 */
export function usePoolMembers(slug: string) {
  return useQuery({
    queryKey: poolKeys.members(slug),
    queryFn: async () => {
      const response = await poolsApi.getMembers(slug);
      return response.data;
    },
    enabled: !!slug,
  });
}

/**
 * Hook to create a new pool
 */
export function useCreatePool() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: (data: { name: string; description?: string; isPublic: boolean }) =>
      poolsApi.create(data),
    onSuccess: (response) => {
      const { pool } = normalizePoolMutationResponse(response.data);
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
      addNotification({
        type: 'success',
        message: 'Bolão criado com sucesso!',
        duration: 3000,
      });
      if (pool?.slug) {
        navigate(`/boloes/${pool.slug}`);
      }
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao criar bolão',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook to update pool
 */
export function useUpdatePool(slug: string) {
  const queryClient = useQueryClient();
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: (data: { name?: string; description?: string; isPublic?: boolean }) =>
      poolsApi.update(slug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: poolKeys.detail(slug) });
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
      addNotification({
        type: 'success',
        message: 'Bolão atualizado!',
        duration: 3000,
      });
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao atualizar bolão',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook to join a pool
 */
export function useJoinPool() {
  const queryClient = useQueryClient();
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: ({ slug, code }: { slug: string; code: string }) =>
      poolsApi.join(slug, code),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: poolKeys.detail(variables.slug) });
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
      queryClient.invalidateQueries({ queryKey: poolKeys.ranking(variables.slug) });
      addNotification({
        type: 'success',
        message: response.data?.message || 'Você entrou no bolão!',
        duration: 3000,
      });
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao entrar no bolão',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook to leave a pool
 */
export function useLeavePool() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: (slug: string) => poolsApi.leave(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
      addNotification({
        type: 'info',
        message: 'Você saiu do bolão',
        duration: 3000,
      });
      navigate('/boloes');
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao sair do bolão',
        duration: 5000,
      });
    },
  });
}
